import { HTTPError } from '@/utils/error';
import {
  MutationCache,
  QueryClient,
  QueryKey,
} from '@tanstack/react-query';
import { toast } from 'sonner';

interface MyMeta extends Record<string, unknown> {
  errorMessage?: string;
  invalidateQueries?: QueryKey | QueryKey[];
  successMessage?: string;
}

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: MyMeta;
  }
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (
          error.message === 'Network Error' ||
          (error instanceof HTTPError &&
            [502, 503, 504].includes(error.status))
        ) {
          return failureCount < 3;
        }
        return false;
      },
    },
    mutations: {
      retry: (failureCount, error) => {
        if (
          error.message === 'Network Error' ||
          (error instanceof HTTPError &&
            [502, 503, 504].includes(error.status))
        ) {
          return failureCount < 3;
        }
        return false;
      },
    },
  },
  mutationCache: new MutationCache({
    onError: (error, variables, context, mutation) => {
      if (mutation.meta?.errorMessage) {
        toast.error(mutation.meta.errorMessage);
      }
    },
    onSuccess: (data, variables, context, mutation) => {
      if (mutation.meta?.successMessage) {
        toast.success(mutation.meta.successMessage);
      }
    },
    onSettled: (data, error, variables, context, mutation) => {
      if (mutation.meta?.invalidateQueries) {
        const queries = mutation.meta.invalidateQueries;

        // Invalidate multiple queries
        if (
          Array.isArray(queries) &&
          queries.length > 0 &&
          Array.isArray(queries[0])
        ) {
          queries.forEach((queryKey: QueryKey) => {
            void queryClient.invalidateQueries({
              queryKey,
            });
          });
        }
        // Invalidate single query
        else {
          void queryClient.invalidateQueries({
            queryKey: queries,
          });
        }
      }
    },
  }),
});
